// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faChevronLeft,
  faPause,
  faPlay,
  faBrain,
  faWallet,
  faUserTie,
  faChartPie,
  faRocket,
  faArrowRight
} from '@fortawesome/pro-thin-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import Text from '@/components/Text';
import Button from '@/components/Button';
import VibeAnimation from '../VibeAnimation';
import { InvestorProfile } from '../../constants';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import Image from 'next/image';

import bloomieAnalytical from '@/assets/bloomie_analytical.png';
import bloomieBalanced from '@/assets/bloomie_balanced.png';
import bloomieCollaborative from '@/assets/bloomie_collaborative.png';
import bloomieContrarian from '@/assets/bloomie_contrarian.png';
import bloomieStrategic from '@/assets/bloomie_strategic.png';
import bloomieVisionary from '@/assets/bloomie_visionary.png';

const BLOOMIE_IMAGES: Record<string, typeof bloomieAnalytical> = {
  ANALYTICAL: bloomieAnalytical,
  BALANCED: bloomieBalanced,
  COLLABORATIVE: bloomieCollaborative,
  CONTRARIAN: bloomieContrarian,
  STRATEGIC: bloomieStrategic,
  VISIONARY: bloomieVisionary,
};

// Distinct pie chart colors matching the results page
const PIE_COLORS = [
  '#3b82f6', // Blue
  '#2dd4bf', // Teal
  '#4ade80', // Green
  '#facc15', // Yellow
  '#fb923c', // Orange
  '#f87171', // Red
  '#a78bfa', // Purple
  '#38bdf8', // Sky
  '#818cf8'  // Indigo
];

interface YourTypeWrappedProps {
  profile: InvestorProfile;
  prefetchData: any;
  onComplete: () => void;
}

const SLIDE_DURATION = 6000; // 6 seconds per slide

const YourTypeWrapped: React.FC<YourTypeWrappedProps> = ({ profile, prefetchData, onComplete }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const pausedTimeRef = useRef<number>(0);
  const pointerStartTimeRef = useRef<number>(0);

  const slides = [
    { id: 'intro', type: 'intro' },
    { id: 'reveal', type: 'reveal' },
    { id: 'why', type: 'why' },
    { id: 'match', type: 'match' },
    { id: 'manager', type: 'manager' },
    { id: 'blueprint', type: 'blueprint' },
    { id: 'final', type: 'final' },
  ];

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(s => s + 1);
      setProgress(0);
      startTimeRef.current = Date.now();
      pausedTimeRef.current = 0;
    } else {
      onComplete();
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(s => s - 1);
      setProgress(0);
      startTimeRef.current = Date.now();
      pausedTimeRef.current = 0;
    }
  };

  useEffect(() => {
    if (isPaused) {
      if (timerRef.current) clearInterval(timerRef.current);
      pausedTimeRef.current = Date.now() - startTimeRef.current;
      return;
    }

    startTimeRef.current = Date.now() - pausedTimeRef.current;

    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const newProgress = Math.min((elapsed / SLIDE_DURATION) * 100, 100);
      setProgress(newProgress);

      if (newProgress >= 100) {
        nextSlide();
      }
    }, 16);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [currentSlide, isPaused]);

  const handlePointerDown = (e: React.PointerEvent) => {
    setIsPaused(true);
    pointerStartTimeRef.current = Date.now();
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    setIsPaused(false);

    const pressDuration = Date.now() - pointerStartTimeRef.current;
    if (pressDuration < 200) {
      // It's a tap
      const { clientX, currentTarget } = e;
      const { left, width } = currentTarget.getBoundingClientRect();
      const x = clientX - left;

      // Left 30% goes back, Right 70% goes forward
      if (x < width * 0.3) {
        prevSlide();
      } else {
        nextSlide();
      }
    }
  };

  const recommendations = prefetchData?.recommendations;
  const primaryMatch = recommendations?.portfolios?.[0];
  const primaryManager = recommendations?.managers?.length > 0 ? recommendations.managers[0] : null;
  const aiPortfolio = prefetchData?.aiPortfolio;
  const portfolioMultiplier = (() => {
    const tickers = aiPortfolio?.indexes?.[0]?.tickers;
    if (!tickers?.length) return 100;
    const sum = tickers.reduce((acc: number, t: any) => acc + (t.percentage || 0), 0);
    return sum > 1.5 ? 1 : 100;
  })();

  const renderSlideContent = () => {
    const slide = slides[currentSlide];

    switch (slide.type) {
      case 'intro':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center gap-5 md:gap-8 animate-in fade-in zoom-in duration-1000">
            <div className="flex flex-col gap-2">
              <Text size="sm" className="text-teal-200 uppercase tracking-[0.3em] font-bold opacity-60">Investment Personality</Text>
              <h1 className="text-2xl md:text-4xl font-bold uppercase leading-none">Your Financial<br />DNA Sequenced</h1>
            </div>
            <div className="max-w-xs flex flex-col gap-3 md:gap-4">
              <Text size="sm" className="md:text-base text-white/60 leading-relaxed">We&apos;ve analyzed your decisions, risk tolerance, and investment philosophy.</Text>
              <Text className="text-teal-200 font-bold uppercase tracking-widest text-xs md:text-sm animate-pulse">Here is your investor archetype.</Text>
            </div>
          </div>
        );

      case 'reveal':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center gap-6 md:gap-12 animate-in fade-in slide-in-from-bottom-8 duration-1000">
            <div className="flex flex-col gap-3 md:gap-4">
              <div className="flex justify-center my-2">
                <div className="relative rounded-[1.75rem] bg-gradient-to-br from-teal-200/10 via-white/5 to-transparent border border-white/[0.08] shadow-[0_0_40px_rgba(45,212,191,0.08)] flex items-center justify-center overflow-hidden size-20 md:size-36 animate-bounce">
                  {BLOOMIE_IMAGES[profile.id] && (
                    <Image
                      src={BLOOMIE_IMAGES[profile.id]}
                      alt={profile.name}
                      width={160}
                      height={160}
                      className="size-16 md:size-36 object-contain drop-shadow-2xl relative z-10"
                    />
                  )}
                </div>
              </div>
              <Text size="xs" className="text-teal-200 uppercase tracking-[0.4em] font-black">Identity Confirmed</Text>
              <h2 className="text-2xl md:text-4xl font-bold uppercase bg-clip-text text-transparent bg-gradient-to-b from-white to-white/40">
                {profile.name}
              </h2>
            </div>
            <div className="flex flex-wrap justify-center gap-2 md:gap-3">
              {profile.traits.map((trait, i) => (
                <span key={trait} className={`px-4 md:px-6 py-1.5 md:py-2 bg-white/5 border border-white/10 rounded-full text-[10px] md:text-xs font-black uppercase tracking-widest animate-in fade-in slide-in-from-right-4 duration-500 delay-${i * 200}`}>
                  {trait}
                </span>
              ))}
            </div>
          </div>
        );

      case 'why':
        return (
          <div className="flex flex-col items-start justify-center h-full gap-4 md:gap-6 px-2 animate-in fade-in slide-in-from-left-8 duration-1000">
            <div className="flex flex-col gap-1.5 md:gap-2">
              <Text size="xs" className="text-teal-200 uppercase tracking-widest font-black opacity-60">The DNA of your success</Text>
              <h2 className="text-2xl md:text-3xl font-bold uppercase">Why you Win</h2>
            </div>
            <div className="grid gap-3 md:gap-4 w-full">
              {profile.detailedAnalysis.slice(0, 2).map((section, i) => (
                <div key={i} className="bg-white/5 border border-white/10 p-4 md:p-5 rounded-[1.25rem] md:rounded-[1.5rem] flex flex-col gap-1.5 md:gap-2">
                  <Text className="text-teal-200 font-black uppercase text-[10px] md:text-xs tracking-widest">{section.title}</Text>
                  <Text size="xs" className="md:text-sm text-white/80 leading-relaxed line-clamp-3">{section.content.replace(/<[^>]*>/g, '').replace(/&apos;/g, "'").replace(/&quot;/g, '"').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')}</Text>
                </div>
              ))}
            </div>
          </div>
        );

      case 'match':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center gap-6 md:gap-10 animate-in fade-in zoom-in duration-1000">
            <div className="flex flex-col gap-2 md:gap-4">
              <Text size="xs" className="text-teal-200 uppercase tracking-widest font-black opacity-60">Your Perfect Institution</Text>
              <h2 className="text-2xl md:text-3xl font-bold uppercase">The Institutional Match</h2>
            </div>

            {primaryMatch && (
              <div className="bg-gradient-to-br from-teal-200 to-teal-400 p-[1px] rounded-[2.5rem] md:rounded-[3rem] shadow-[0_0_50px_rgba(153,246,228,0.2)] w-full max-w-sm">
                <div className="bg-black/90 rounded-[2.5rem] md:rounded-[3rem] p-5 md:p-6 flex flex-col items-center gap-4 md:gap-5 max-h-[70vh] md:max-h-[60vh] overflow-hidden">
                  <div className="size-12 md:size-20 rounded-2xl md:rounded-3xl bg-white/5 flex items-center justify-center overflow-hidden flex-none">
                    {primaryMatch.portfolio.iconUrl ? (
                      <img src={primaryMatch.portfolio.iconUrl} alt="" className="w-full h-full object-contain p-3 md:p-4" />
                    ) : (
                      <FontAwesomeIcon icon={faWallet as IconProp} className="size-6 md:size-10 text-white/20" />
                    )}
                  </div>
                  <div className="flex flex-col gap-1 flex-none">
                    <Text size="lg" className="md:text-lg font-bold text-white uppercase">{primaryMatch.portfolio.fundName}</Text>
                    <div className="px-4 py-1 bg-teal-200 text-black rounded-full text-[10px] font-black uppercase tracking-widest mx-auto">
                      Top Match
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto w-full px-2 md:px-4 min-h-0" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.1) transparent' }}>
                    <Text size="xs" className="md:text-sm text-white/60 leading-relaxed">
                      &quot;{primaryMatch.personalizedExplanation}&quot;
                    </Text>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'manager':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center gap-0 relative">
            {/* Sparkle / Star Decorations */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none select-none animate-in fade-in duration-1000">
              <span className="absolute top-[6%] left-[15%] text-2xl text-white/20 animate-pulse" style={{ animationDuration: '2s' }}>✦</span>
              <span className="absolute top-[10%] right-[18%] text-lg text-emerald-300/15 animate-pulse" style={{ animationDuration: '3s', animationDelay: '0.6s' }}>✧</span>
              <span className="absolute top-[3%] right-[8%] text-xl text-white/10 animate-pulse" style={{ animationDuration: '2.5s', animationDelay: '1.2s' }}>✦</span>
              <span className="absolute bottom-[25%] left-[6%] text-sm text-emerald-200/10 animate-pulse" style={{ animationDuration: '3.5s', animationDelay: '1.8s' }}>✧</span>
              <span className="absolute bottom-[30%] right-[6%] text-sm text-white/10 animate-pulse" style={{ animationDuration: '2s', animationDelay: '2.4s' }}>✦</span>
              <span className="absolute top-[40%] left-[50%] text-xs text-emerald-300/10 animate-pulse" style={{ animationDuration: '4s', animationDelay: '0.3s' }}>✧</span>

              {/* Soft glow orbs */}
              <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl" />
              <div className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-96 h-96 bg-emerald-900/15 rounded-full blur-3xl" />
            </div>

            {primaryManager && (
              <div className="relative z-10 flex flex-col items-center gap-4 md:gap-6 w-full max-w-sm">
                {/* Section Label */}
                <div className="flex flex-col gap-0.5 md:gap-1 animate-in fade-in duration-700">
                  <Text size="xs" className="text-emerald-300/50 uppercase tracking-[0.3em] font-black text-[10px] md:text-xs">Your Market Twin</Text>
                  <Text size="xs" className="text-white/30 uppercase tracking-[0.2em] font-bold text-[10px] md:text-xs">The Mindset Match</Text>
                </div>

                {/* Overlapping Avatar Circles */}
                <div className="flex items-center justify-center">
                  {/* User's bloomie avatar */}
                  <div className="size-[4.5rem] md:size-[6.5rem] rounded-full bg-gradient-to-br from-teal-200/20 via-emerald-500/10 to-transparent border-[2.5px] md:border-[3px] border-white/25 shadow-2xl shadow-emerald-500/20 overflow-hidden flex items-center justify-center z-10 animate-in slide-in-from-left-8 fade-in duration-700">
                    {BLOOMIE_IMAGES[profile.id] && (
                      <Image
                        src={BLOOMIE_IMAGES[profile.id]}
                        alt={profile.name}
                        width={96}
                        height={96}
                        className="size-[3.5rem] md:size-[5.5rem] object-contain drop-shadow-lg"
                      />
                    )}
                  </div>
                  {/* Manager photo */}
                  <div className="-ml-5 md:-ml-6 size-[4.5rem] md:size-[6.5rem] rounded-full bg-black/60 border-[2.5px] md:border-[3px] border-white/25 shadow-2xl shadow-emerald-500/20 overflow-hidden flex items-center justify-center animate-in slide-in-from-right-8 fade-in duration-700">
                    {primaryManager.manager.iconUrl ? (
                      <img src={primaryManager.manager.iconUrl} alt={primaryManager.manager.name} className="w-full h-full object-cover" />
                    ) : (
                      <FontAwesomeIcon icon={faUserTie as IconProp} className="size-8 md:size-10 text-white/20" />
                    )}
                  </div>
                </div>

                {/* "It's a Match!" Hero Text */}
                <div className="text-center flex flex-col items-center gap-1 md:gap-1.5 animate-in fade-in zoom-in duration-500 delay-500">
                  <h2
                    className="text-[1.75rem] md:text-[2.75rem] leading-none font-black text-white drop-shadow-[0_2px_20px_rgba(16,185,129,0.35)]"
                    style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: 'italic' }}
                  >
                    It&apos;s a Match!
                  </h2>
                  <p className="text-white/45 text-xs md:text-base font-medium">
                    You matched with{' '}
                    <span className="text-emerald-300 font-bold">{primaryManager.manager.name}</span>
                  </p>
                </div>

                {/* Tags: Fund, Style, Risk, Score */}
                <div className="flex flex-wrap items-center justify-center gap-1 md:gap-1.5 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-700">
                  {primaryManager.manager.fundName && (
                    <span className="px-2.5 md:px-3 py-0.5 md:py-1 bg-white/10 backdrop-blur-sm text-white/70 rounded-full text-[9px] md:text-[10px] font-bold uppercase tracking-wider border border-white/10">
                      {primaryManager.isDirectMatch ? 'Manages ' : ''}{primaryManager.manager.fundName}
                    </span>
                  )}
                  {primaryManager.manager.investmentStyle && (
                    <span className="px-2.5 md:px-3 py-0.5 md:py-1 bg-emerald-400/15 text-emerald-300 rounded-full text-[9px] md:text-[10px] font-bold uppercase tracking-wider border border-emerald-400/20">
                      {primaryManager.manager.investmentStyle}
                    </span>
                  )}
                  {primaryManager.manager.riskTolerance && (
                    <span className="px-2.5 md:px-3 py-0.5 md:py-1 bg-white/5 text-white/50 rounded-full text-[9px] md:text-[10px] font-bold uppercase tracking-wider border border-white/5">
                      {primaryManager.manager.riskTolerance}
                    </span>
                  )}
                </div>

                {/* Match Reason */}
                <div className="bg-white/[0.07] backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-5 w-full text-center border border-white/10 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-1000">
                  <Text size="[10px]" className="text-emerald-300/50 uppercase tracking-[0.2em] font-black mb-1.5 md:mb-2 block text-[9px] md:text-[10px]">
                    Why You&apos;re a Match
                  </Text>
                  <Text size="xs" className="md:text-sm text-white/75 leading-relaxed font-medium line-clamp-3 md:line-clamp-4">
                    {primaryManager.matchReason}
                  </Text>
                </div>
              </div>
            )}
          </div>
        );

      case 'blueprint':
        const pieData = aiPortfolio?.indexes?.[0]?.tickers?.slice(0, 8).map(t => ({
          name: t.ticker,
          value: (t.percentage || 0) * portfolioMultiplier
        })) || [];
        const stockList = aiPortfolio?.indexes?.[0]?.tickers?.slice(0, 6) || [];

        return (
          <div className="flex flex-col items-center justify-center h-full text-center gap-4 md:gap-3 animate-in fade-in slide-in-from-bottom-12 duration-1000">
            <div className="flex flex-col gap-2 md:gap-1.5">
              <Text size="xs" className="text-teal-200 uppercase tracking-widest font-black opacity-60">Bloomie Custom Portfolio</Text>
              <h2 className="text-2xl md:text-3xl font-bold uppercase">Your Personal Blueprint</h2>
              {aiPortfolio?.isSampleData && (
                <Text size="xs" className="text-yellow-400 font-bold uppercase tracking-widest bg-yellow-400/10 px-3 py-1 rounded-full border border-yellow-400/20 inline-block w-fit mx-auto text-[10px] md:text-xs">
                  Sample Data (Quota Limit)
                </Text>
              )}
            </div>

            {pieData.length > 0 ? (
              <>
                <div className="w-full max-w-[12rem] md:max-w-xs aspect-square relative flex items-center justify-center">
                  <div className="absolute inset-0 bg-teal-200/5 rounded-full animate-pulse" />
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius="38%"
                        outerRadius="56%"
                        paddingAngle={2}
                        dataKey="value"
                        stroke="none"
                      >
                        {pieData.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} className="hover:opacity-80 transition-opacity outline-none" />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute flex flex-col items-center">
                    <FontAwesomeIcon icon={faChartPie as IconProp} className="size-5 md:size-6 text-teal-200/40 mb-1" />
                    <Text className="text-[9px] md:text-[10px] font-black uppercase tracking-tighter text-white/20">Custom Index</Text>
                  </div>
                </div>

                <div className="flex flex-col gap-3 md:gap-2 w-full max-w-sm md:max-w-md">
                  {/* Stock List */}
                  {stockList.length > 0 && (
                    <div className="bg-black/20 rounded-xl md:rounded-2xl border border-white/5 overflow-hidden w-full">
                      <ul className="flex flex-col md:flex-row md:flex-wrap md:justify-center md:gap-0 md:p-1.5">
                        {stockList.map((stock: any, i: number) => (
                          <li key={i} className={`border-b md:border-0 md:border-r border-white/5 last:border-0 md:flex-[0_0_auto]`}>
                            <div className="flex w-full items-center justify-between md:flex-row md:justify-center md:gap-2 p-2.5 md:p-1.5 hover:bg-white/5 transition-all">
                              <div className="flex items-center gap-2.5 md:gap-1.5">
                                <div
                                  className="size-2.5 md:size-2.5 rounded-sm shadow-sm flex-none"
                                  style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                                />
                                <Text className="font-bold text-white/90 text-xs md:text-[10px]">
                                  {stock.ticker}
                                </Text>
                              </div>
                              <Text className="text-white/80 font-medium text-xs md:text-[10px]">
                                {((stock.percentage || 0) * portfolioMultiplier).toFixed(1)}%
                              </Text>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center gap-4 md:gap-6 py-8 md:py-12">
                <FontAwesomeIcon icon={faRocket as IconProp} className="size-10 md:size-12 text-white/10 animate-pulse" />
                <Text size="xs" className="md:text-sm text-white/40 px-8 md:px-12">The AI engine is still calculating your custom blueprint. You&apos;ll see it in the final results.</Text>
              </div>
            )}
          </div>
        );

      case 'final':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center gap-8 md:gap-12 animate-in fade-in duration-1000">
            <div className="flex flex-col gap-4 md:gap-6">
              <div className="size-14 md:size-20 rounded-2xl md:rounded-3xl bg-teal-200 text-black flex items-center justify-center mx-auto shadow-[0_0_40px_rgba(153,246,228,0.4)]">
                <FontAwesomeIcon icon={faRocket as IconProp} className="size-7 md:size-10" />
              </div>
              <div className="flex flex-col gap-2 md:gap-3">
                <h2 className="text-3xl md:text-4xl font-bold uppercase">Victory Awaits</h2>
                <Text size="sm" className="md:text-base text-white/60 max-w-xs mx-auto">Your identity is clear. Your path is built. It&apos;s time to conquer the market with Bloom.</Text>
              </div>
            </div>

            <Button
              onClick={onComplete}
              className="w-full h-12 md:h-16 rounded-[1.5rem] md:rounded-[2rem] bg-teal-200 text-black hover:bg-teal-300 font-black uppercase tracking-[0.2em] text-sm md:text-base flex items-center justify-center gap-3 md:gap-4 group pointer-events-auto"
            >
              See Full Blueprint
              <FontAwesomeIcon icon={faArrowRight as IconProp} className="size-3.5 md:size-4 group-hover:translate-x-2 transition-transform" />
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black flex flex-col items-center overflow-hidden touch-none select-none"
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
    >
      <VibeAnimation vibe={0.2 + (currentSlide * 0.1)} color="rgb(153, 246, 228)" />

      {/* Top Progress Bars */}
      <div className="absolute top-0 left-0 right-0 z-[110]">
        <div className="p-4 pt-12 flex gap-1.5">
          {slides.map((_, i) => (
            <div key={i} className="h-1 flex-1 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-teal-200 transition-all duration-[16ms] ease-linear"
                style={{
                  width: i < currentSlide ? '100%' : i === currentSlide ? `${progress}%` : '0%'
                }}
              />
            </div>
          ))}
        </div>
        {/* Navigation Hint */}
        <div className="px-4 pb-3 flex justify-center items-center opacity-40 pointer-events-auto">
          <Text size="xs" className="text-white/40 uppercase tracking-widest font-bold">
            {isPaused ? 'Paused' : 'Tap to navigate • Hold to pause'}
          </Text>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 w-full max-w-md relative z-[105] px-5 md:px-6 py-16 md:py-20 flex flex-col pointer-events-none">
        {renderSlideContent()}
      </div>

      {/* Paused Indicator Overlay */}
      {isPaused && (
        <div className="absolute inset-0 z-[109] flex items-center justify-center pointer-events-none animate-in fade-in duration-200">
          <div className="bg-black/40 backdrop-blur-sm p-6 rounded-3xl border border-white/10">
            <FontAwesomeIcon icon={faPause as IconProp} className="size-8 text-white/80" />
          </div>
        </div>
      )}

    </div>
  );
};

export default YourTypeWrapped;

